import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 40);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(event.target as Node)) {
        setIsGuidesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsCategoryOpen(false);
    setIsGuidesOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  const handleCategorySelect = useCallback((url: string) => {
    setIsCategoryOpen(false);
    setIsMobileOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight whitespace-nowrap cursor-pointer"
            >
              CSSSpreadsheet<span className="text-primary-500">.casa</span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === '/'
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Home
              </Link>

              <div ref={guidesRef} className="relative">
                <button
                  onClick={() => { setIsGuidesOpen(!isGuidesOpen); setIsCategoryOpen(false); }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    location.pathname.startsWith('/article/')
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  Shopping Guides
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isGuidesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isGuidesOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-2xl border border-background-200 py-2 z-50">
                    <Link
                      to="/article/cssbuy-spreadsheet-guide"
                      onClick={() => setIsGuidesOpen(false)}
                      className="block w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      <span className="font-semibold">Beginner Guide</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">Complete CSSBuy spreadsheet roadmap for new buyers</span>
                    </Link>
                    <Link
                      to="/article/shopping-guide"
                      onClick={() => setIsGuidesOpen(false)}
                      className="block w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      <span className="font-semibold">Shopping Guide</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">Step-by-step purchase walkthrough with tips and tricks</span>
                    </Link>
                    <Link
                      to="/article/how-to-qc"
                      onClick={() => setIsGuidesOpen(false)}
                      className="block w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      <span className="font-semibold">QC Master Guide</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">How to inspect quality check photos like a pro</span>
                    </Link>
                  </div>
                )}
              </div>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsGuidesOpen(false); }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isCategoryOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-2xl border border-background-200 py-2 max-h-[420px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <button
                        key={cat.seq}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setIsMobileOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[300px] bg-background-50 overflow-y-auto pt-20">
            <div className="px-5 py-4">
              <Link
                to="/"
                onClick={() => setIsMobileOpen(false)}
                className={`block px-4 py-3 rounded-xl text-sm font-semibold mb-1 transition-colors duration-150 cursor-pointer ${
                  location.pathname === '/' ? 'bg-foreground-950 text-background-50' : 'text-foreground-700'
                }`}
              >
                <i className="ri-home-4-line mr-2"></i>Home
              </Link>

              <div className="mt-6 mb-3 px-4">
                <p className="text-xs text-foreground-500 font-label tracking-widest uppercase">Shopping Guides</p>
              </div>
              <Link
                to="/article/cssbuy-spreadsheet-guide"
                onClick={() => setIsMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                Beginner Guide
              </Link>
              <Link
                to="/article/shopping-guide"
                onClick={() => setIsMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                Shopping Guide
              </Link>
              <Link
                to="/article/how-to-qc"
                onClick={() => setIsMobileOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
              >
                QC Master Guide
              </Link>

              <div className="mt-6 mb-3 px-4">
                <p className="text-xs text-foreground-500 font-label tracking-widest uppercase">Categories</p>
              </div>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.seq}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="mt-8 px-4 pt-6 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  CSSBuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}